// App.js

import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import Login from './Login';                          // Admin login
import UserLogin from './UserLogin';                  // NEW - User login
import ProtectedPage from './Protected';
import PortManagement from './features/port-management/PortManagement';
import SendMessages from './SendMessages';
import PortStatus from './PortStatus';
import PasswordManagement from './PasswordManagement';
import AdvicePasswordManagement from './AdvicePasswordManagement';
import AdvicePasswordsDashboard from './AdvicePasswordsDashboard';
import TTInsights from './TTInsights';
import UserDashboard from './features/user-dashboard/UserDashboard';
import UserGatewaysManagement from './features/gateway-management/UserGatewaysManagement';
import UserGatewayPage from './features/gateway-management/UserGatewayPage';
import SimInsertionGuide from './features/gateway-management/SimInsertionGuide';
import VaultUserSignupList from './features/vault-user-signup/VaultUserSignupList';
import KeepAliveDashboard from './features/keep-alive/KeepAliveDashboard';
import Navigation from './components/Navigation';

/* ============================
   Auth Guards
   ============================ */

const ProtectedRoute = ({ children, role, ffStaffAllowed = true }) => {
  const token = localStorage.getItem('token');
  const currentRole = localStorage.getItem('role');
  const staffRole = localStorage.getItem('staff_role');
  const location = useLocation();

  if (!token) {
    return <Navigate to="/userlogin" state={{ from: location }} replace />;
  }

  if (role && currentRole !== role) {
    const redirectTo = currentRole === 'user' ? '/user' : '/protected';
    return <Navigate to={redirectTo} replace />;
  }

  // Block FF Staff from routes they are not permitted to access
  if (!ffStaffAllowed && currentRole === 'staff' && staffRole === 'FF Staff') {
    return <Navigate to="/protected" replace />;
  }

  return children;
};

/* ============================
   App (Routes)
   ============================ */

const App = () => {
  // Default landing: if logged in send to role home; else to /userlogin
  const RoleHome = () => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token) return <Navigate to="/userlogin" replace />;
    return <Navigate to={role === 'user' ? '/user' : '/protected'} replace />;
  };

  return (
    <Router>
      <div>
        <Navigation />
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />            {/* Admin login */}
          <Route path="/userlogin" element={<UserLogin />} />    {/* NEW User login */}

          {/* Default → role home (if logged in) or user login */}
          <Route path="/" element={<RoleHome />} />

          {/* Staff-only routes */}
          <Route
            path="/protected"
            element={
              <ProtectedRoute role="staff">
                <ProtectedPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ports"
            element={
              <ProtectedRoute role="staff">
                <PortManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/send-messages"
            element={
              <ProtectedRoute role="staff" ffStaffAllowed={false}>
                <SendMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/port-status"
            element={
              <ProtectedRoute role="staff">
                <PortStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/passwords"
            element={
              <ProtectedRoute role="staff" ffStaffAllowed={false}>
                <PasswordManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/advice-passwords"
            element={
              <ProtectedRoute role="staff">
                <AdvicePasswordManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/advice-passwords-dashboard"
            element={
              <ProtectedRoute role="staff">
                <AdvicePasswordsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/archived-passwords"
            element={
              <ProtectedRoute role="staff" ffStaffAllowed={false}>
                <PasswordManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-gateways"
            element={
              <ProtectedRoute role="staff" ffStaffAllowed={false}>
                <UserGatewaysManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tt-insights"
            element={
              <ProtectedRoute role="staff" ffStaffAllowed={false}>
                <TTInsights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-signup"
            element={
              <ProtectedRoute role="staff" ffStaffAllowed={false}>
                <VaultUserSignupList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/keep-alive"
            element={
              <ProtectedRoute role="staff" ffStaffAllowed={false}>
                <KeepAliveDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sim-insertion-guide"
            element={
              <ProtectedRoute role="staff">
                <SimInsertionGuide />
              </ProtectedRoute>
            }
          />

          {/* User-only routes */}
          <Route
            path="/user"
            element={
              <ProtectedRoute role="user">
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-gateway"
            element={
              <ProtectedRoute role="user">
                <UserGatewayPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
